
import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { supabase } from "@/lib/supabase";

const SubscriptionCancel = () => {
  const [isProcessing, setIsProcessing] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshProfile } = useAuth();
  
  // Check for error message or code in URL
  const searchParams = new URLSearchParams(location.search);
  const errorCode = searchParams.get('error_code');
  const errorMessage = searchParams.get('error_message');
  const subscriptionId = searchParams.get('subscription_id');

  useEffect(() => {
    const processCancellation = async () => {
      try {
        // Show appropriate toast message based on error information
        if (errorCode || errorMessage) {
          toast({
            title: "Payment Failed",
            description: errorMessage || "There was an issue with the payment process.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Payment Cancelled",
            description: "Your payment was cancelled. No charges have been made."
          });
        }
        
        // If we have a user and subscription ID, clean up any pending subscription
        if (user && subscriptionId) {
          console.log("Cleaning up pending subscription:", subscriptionId);
          
          // Call edge function to ensure PayPal cancellation and database cleanup
          try {
            const { data, error: cancelError } = await supabase.functions.invoke('cancel-subscription', {
              body: { 
                subscriptionId,
                userId: user.id, 
                forceCleanup: true // Special flag to indicate we're cleaning up a cancelled flow
              }
            });
            
            if (cancelError) {
              console.error("Error calling cancel-subscription:", cancelError);
            } else {
              console.log("PayPal cleanup result:", data);
            }
          } catch (err) {
            console.error("Exception in cancel-subscription call:", err);
          }
          
          // Refresh the user profile to ensure correct subscription state
          await refreshProfile();
        }
      } catch (err) {
        console.error("Error in subscription cancellation:", err);
      } finally {
        setIsProcessing(false);
      }
    };

    processCancellation();
  }, [toast, user, subscriptionId, refreshProfile, errorCode, errorMessage]);

  // Provide clear button to try again
  const handleTryAgain = () => {
    navigate('/pricing');
  };

  return (
    <div className="container max-w-md mx-auto py-16 px-4 text-center">
      <div className="flex justify-center mb-6">
        <XCircle className="h-16 w-16 text-red-500" />
      </div>
      <h1 className="text-3xl font-bold mb-4">
        {errorCode ? "Payment Failed" : "Payment Cancelled"}
      </h1>
      
      {isProcessing ? (
        <div className="flex flex-col items-center justify-center gap-2 mb-8">
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Processing your cancellation...</span>
          </div>
        </div>
      ) : (
        <p className="text-gray-600 mb-8">
          {errorCode 
            ? "There was an issue processing your payment. No charges have been made to your account."
            : "Your payment process was cancelled. No charges have been made to your account."}
        </p>
      )}
      
      <div className="flex flex-col gap-4">
        <Button onClick={handleTryAgain}>
          Try Again
        </Button>
        <Button variant="outline" asChild>
          <Link to="/">Go to Home</Link>
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionCancel;
